import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Menu, X, User, LogOut } from 'lucide-react';
import LanguageSwitcher from '../LanguageSwitcher';
import { shouldShowAuth, shouldShowWaitingList, isAuthenticated, signOut } from '../../../../utils/auth';
import { useI18n } from '../../../../hooks/useInternationalization';
import BetaBanner from '../../BetaBanner';
import './Header.css';

const Header = () => {
  const { t } = useI18n();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [userAuthenticated, setUserAuthenticated] = useState(false);
  const [isHeaderHidden, setIsHeaderHidden] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const lastScrollYRef = useRef(0);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    lastScrollYRef.current = window.scrollY || 0;

    const isLandingPage = location.pathname === '/';

    const handleScroll = () => {
      const currentY = window.scrollY;
      const lastY = lastScrollYRef.current;
      const isScrollingDown = currentY > lastY;

      setIsScrolled(currentY > 10);

      if (isLandingPage) {
        setIsCollapsed(currentY > 80);
        if (isScrollingDown) {
          setIsMenuOpen(false);
        }
        setIsHeaderHidden(false);
      } else {
        setIsCollapsed(false);
        if (currentY > 120 && isScrollingDown) {
          setIsHeaderHidden(true);
          setIsMenuOpen(false);
        } else if (!isScrollingDown || currentY <= 120) {
          setIsHeaderHidden(false);
        }
      }

      lastScrollYRef.current = currentY;
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();

    return () => window.removeEventListener('scroll', handleScroll);
  }, [location.pathname]);

  useEffect(() => {
    // Check authentication status
    setUserAuthenticated(isAuthenticated());
  }, [location.pathname]);

  const isActive = (path) => {
    return location.pathname === path;
  };

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setIsMenuOpen(false);
    }
  };

  const handleNavigation = (sectionId, fallbackPath = null) => {
    // If we're on the home page, scroll to section
    if (location.pathname === '/') {
      scrollToSection(sectionId);
    } else {
      // Navigate to home page with hash, or to fallback path
      if (fallbackPath) {
        navigate(fallbackPath);
      } else {
        navigate(`/#${sectionId}`);
        // After navigation, scroll to section
        setTimeout(() => {
          const element = document.getElementById(sectionId);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
          }
        }, 100);
      }
    }
    setIsMenuOpen(false);
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      setUserAuthenticated(false);
      setIsMenuOpen(false);
      // Redirect will be handled by the auth utility
    } catch (error) {
      console.error('Sign out failed:', error);
    }
  };

  const navItems = [
    { id: 'how-it-works', label: t('footer.links.howItWorks'), action: () => handleNavigation('how-it-works') },
    { id: 'pricing', label: t('footer.links.pricing'), isLink: true, path: '/pricing' },
    { id: 'faq', label: t('footer.links.faq'), action: () => handleNavigation('faq') },
    { id: 'contact', label: t('footer.links.contact'), isLink: true, path: '/contact' }
  ];

  return (
    <motion.header 
      className={`header ${isScrolled ? 'scrolled' : ''} ${isHeaderHidden ? 'hidden' : ''} ${isCollapsed ? 'collapsed' : ''}`}
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6 }}
    >
      <div className="container">
        <nav className="nav">
          {/* Logo */}
          <Link to="/" className="logo-link">
            <div className="logo">
              <img src="/logo.png" alt="StudySmith" className="logo-image" />
              <BetaBanner variant="default" />
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="nav-menu desktop-nav">
            {navItems.map((item) => (
              item.isLink ? (
                <Link
                  key={item.id}
                  to={item.path}
                  className={`nav-link ${isActive(item.path) ? 'active' : ''}`}
                >
                  {item.label}
                </Link>
              ) : (
                <button
                  key={item.id}
                  onClick={item.action}
                  className="nav-link"
                >
                  {item.label}
                </button>
              )
            ))}
          </div>

          {/* Action Buttons */}
          <div className="nav-actions">
            <LanguageSwitcher />
            
{userAuthenticated ? (
              <>
                <Link 
                  to="/dashboard" 
                  className={`btn btn-outline btn-small ${isActive('/dashboard') ? 'active' : ''}`}
                >
                  <User size={16} />
                  {t('header.actions.dashboard')}
                </Link>
                <button 
                  onClick={handleSignOut}
                  className="btn btn-secondary btn-small"
                >
                  <LogOut size={16} />
                  {t('header.actions.signOut')}
                </button>
              </>
            ) : shouldShowAuth() ? (
              <Link 
                to="/signup" 
                className={`btn btn-primary btn-small btn-signup-enhanced ${isActive('/signup') ? 'active' : ''}`}
              >
                <User size={16} />
                {t('header.actions.signUp')}
              </Link>
            ) : shouldShowWaitingList() ? (
              <Link 
                to="/waiting-list" 
                className={`btn btn-primary btn-small ${isActive('/waiting-list') ? 'active' : ''}`}
              >
                {t('header.actions.waitlist')}
              </Link>
            ) : null}

            {/* Mobile Menu Toggle */}
            <button
              className="mobile-menu-toggle"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              aria-label="Toggle menu"
            >
              {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </nav>

        {/* Mobile Navigation */}
        <motion.div 
          className={`mobile-nav ${isMenuOpen ? 'open' : ''}`}
          initial={false}
          animate={{
            height: isMenuOpen ? 'auto' : 0,
            opacity: isMenuOpen ? 1 : 0
          }}
          transition={{ duration: 0.3 }}
        >
          <div className="mobile-nav-content">
            {navItems.map((item) => (
              item.isLink ? (
                <Link
                  key={item.id}
                  to={item.path}
                  className={`mobile-nav-link ${isActive(item.path) ? 'active' : ''}`}
                  onClick={() => setIsMenuOpen(false)}
                >
                  {item.label}
                </Link>
              ) : (
                <button
                  key={item.id}
                  onClick={item.action}
                  className="mobile-nav-link"
                >
                  {item.label}
                </button>
              )
            ))}
            
            <div className="mobile-nav-actions">
              {userAuthenticated ? (
                <>
                  <Link 
                    to="/dashboard" 
                    className="btn btn-outline"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <User size={16} />
                    {t('header.actions.dashboard')}
                  </Link>
                  <button 
                    onClick={handleSignOut}
                    className="btn btn-secondary"
                  >
                    <LogOut size={16} />
                    {t('header.actions.signOut')}
                  </button>
                </>
              ) : shouldShowAuth() ? (
                <Link 
                  to="/signup" 
                  className="btn btn-primary btn-signup-enhanced"
                  onClick={() => setIsMenuOpen(false)}
                >
                  <User size={16} />
                  {t('header.actions.signUp')}
                </Link>
              ) : shouldShowWaitingList() ? (
                <Link 
                  to="/waiting-list" 
                  className="btn btn-primary"
                  onClick={() => setIsMenuOpen(false)}
                >
                  {t('header.actions.waitlist')}
                </Link>
              ) : null}
              
              {/* Language Switcher for Mobile */}
              <div className="mobile-language-switcher">
                <LanguageSwitcher />
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </motion.header>
  );
};

export default Header; 
